import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Phone, Mail, ArrowRight, FileText } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { SeoContentSection } from "../components/Seo";
import { JOBCENTRE_INTRO, JOBCENTRE_FAQ } from "../data/homeContent";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal, MagneticButton } from "../components/shared/kursusUi";

const JobcenterPage = () => {
  const navigate = useNavigate();

  const services = [
    "Fleksible holdstørrelser og datoer",
    "Hurtig opstart - ofte inden for 2 uger",
    "Opfølgning og rapportering",
    "Dedikeret kontaktperson",
    "Skræddersyede forløb efter behov"
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <Reveal><Overline light>Jobcentre & A-kasser</Overline></Reveal>
            <Reveal i={1}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.05] mb-6">
                Samarbejde med Jobcentre & A-kasser
              </h1>
            </Reveal>
            <Reveal i={2}>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                Vi tilbyder effektive løsninger til jobcentre og A-kasser. Få dine ledige hurtigt i gang med kompetencegivende kurser inden for vognmandsbranchen.
              </p>
            </Reveal>
            <Reveal i={3}>
              <MagneticButton
                onClick={() => navigate('/kontakt')}
                className="kursus-glow-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white"
                style={{ backgroundColor: PRIMARY }}
              >
                Kontakt os <ArrowRight className="h-5 w-5" />
              </MagneticButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Services */}
          <Reveal className="bg-white rounded-3xl border border-slate-200/80 p-8 md:p-10 shadow-[0_8px_30px_rgba(15,40,90,0.06)]">
            <Overline>Vores tilbud</Overline>
            <h2 className="font-display text-2xl md:text-3xl font-medium mb-6 text-slate-900">
              Vores tilbud til jer
            </h2>
            <div className="space-y-4">
              {services.map((service, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 flex-shrink-0" style={{ color: PRIMARY }} />
                  <p className="text-slate-700 leading-relaxed">{service}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Pricing Info */}
          <Reveal className="rounded-3xl p-8 md:p-10 border" style={{ backgroundColor: 'hsl(210,100%,45%,0.06)', borderColor: 'hsl(210,100%,45%,0.18)' }}>
            <div className="flex items-start gap-4">
              <FileText className="h-8 w-8 flex-shrink-0" style={{ color: PRIMARY }} />
              <div>
                <h3 className="font-display text-xl md:text-2xl font-medium mb-2 text-slate-900">
                  Priser
                </h3>
                <p className="text-slate-700 mb-4 leading-relaxed">
                  Priserne på AMU-forløbene, taxauddannelsen, VB GU, VB EU og evakueringsstol,
                  følger gældende takstkatalog for AMU. De øvrige forløb har fast kursuspris,
                  som fremgår af den enkelte kursusside.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Kontakt os for at høre mere om specifikke kurser og priser. Vi hjælper gerne med at finde de rette kurser til jeres borgere.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Contact */}
          <Reveal className="bg-white rounded-3xl border border-slate-200/80 p-8 md:p-10 shadow-[0_8px_30px_rgba(15,40,90,0.06)]">
            <Overline>Kontakt</Overline>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-4 text-slate-900">
              Kontakt os
            </h3>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Er du sagsbehandler eller ansvarlig for opkvalificering? Kontakt os for at høre mere om vores tilbud og hvordan vi kan hjælpe jeres borgere.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+4555292828"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-white transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: PRIMARY }}
              >
                <Phone className="h-5 w-5" /> Ring til os
              </a>
              <a
                href="mailto:kundeservice@votra.dk"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <Mail className="h-5 w-5" /> Send email
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <SeoContentSection intro={JOBCENTRE_INTRO} faq={JOBCENTRE_FAQ} testid="jobcentre-seo-content" />

      <Footer />
    </div>
  );
};

export default JobcenterPage;
